<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ funcionario.id ? "Editar Funcionário" : "Novo Funcionário" }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm 
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarFuncionario"
      @cancel="voltar"
    >
      <FormSection title="Dados do Funcionário">
        <div class="d-flex align-items-center gap-3">
          <!-- Pessoa Física -->
          <div class="input-with-button">
            <InputField
              id="pessoa-fisica"
              label="Pessoa <span class='required'>*</span>"
              v-model="funcionario.pessoa_fisica_nome"
              :disabled="true"
              :class="{ 'error-input': errors.pessoa_fisica_nome }"
              width="300px"
            />
            <button
              class="btn btn-primary pessoa-icon-btn"
              type="button"
              @click="abrirModal"
            >
              <i class="bi bi-person"></i>
            </button>
          </div>

          <!-- Cargo -->
          <div class="form-group">
            <SelectField
              id="cargo"
              v-model="funcionario.cargo_id"
              label="Cargo <span class='required'>*</span>"
              :options="[
                { value: '', text: 'Selecione um cargo', disabled: true },
                ...cargos.map(cargo => ({ value: cargo.id, text: cargo.nome }))
              ]"
              :class="{ 'error-input': errors.cargo_id }"
              required
            />
          </div>

          <!-- Status -->
          <div class="form-group">
            <label for="status">Status</label>
            <div class="form-switch">
              <input
                type="checkbox"
                id="status"
                v-model="funcionario.ativo"
                class="form-check-input"
              />
              <label for="status" class="form-check-label">
                {{ funcionario.ativo === true ? "Ativo" : "Inativo" }}
              </label>
            </div>
          </div>
        </div>
      </FormSection>
    </BaseForm>

    <!-- Modal de Seleção de Pessoa Física -->
    <CarregarPessoaFisica 
      :mostrar="showModal"
      @selecionar="selecionarProprietario"
      @fechar="showModal = false"
    />

  </div>
</template>
  
<script>
  import { reactive, ref, computed, onMounted, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import axios from "@/services/axios";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  import CarregarPessoaFisica from "@/components/modals/CarregarPessoaFisica.vue"; // ✅ Importação do modal
  import InputField from "@/components/Layout/InputField.vue";
  import SelectField from "@/components/Layout/SelectField.vue";
  
  export default {
    components: { BaseForm, FormSection, CarregarPessoaFisica, InputField, SelectField },
  
    setup() {
      const route = useRoute();
      const router = useRouter();
  
      const funcionario = reactive({
        cargo_id: "",
        ativo: true,
        pessoa_fisica_id: "",
        pessoa_fisica_nome: "",
        id: null,
      });
  
      const cargos = ref([]);
      const showModal = ref(false);
      const isSubmitting = ref(false);
      const errors = reactive({ backend: null });
  
      const carregarCargos = async () => {
        try {
          const { data } = await axios.get('/cadastros/cargos-funcionarios', {
            params: { page: 1, limit: 10000 }
          });

          const lista = data?.data ?? data ?? [];
          // Filtra apenas ativos (se o campo existir) e garante array
          cargos.value = Array.isArray(lista)
            ? lista.filter(c => c.ativo !== false) // mantém se ativo=true ou undefined
            : [];

          // Ordena por nome do cargo (ajuste a chave se for diferente)
          cargos.value.sort((a, b) =>
            String(a.nome ?? a.cargo ?? '')
              .localeCompare(String(b.nome ?? b.cargo ?? ''), 'pt-BR', {
                sensitivity: 'base',
                numeric: true
              })
          );
        } catch (error) {
          console.error('Erro ao carregar cargos:', error);
          cargos.value = [];
        }
      };
  
      const carregarFuncionario = async () => {
        const funcionarioId = route.params.id;
        if (!funcionarioId) return;
  
        try {
          const response = await axios.get(`/cadastros/funcionarios/${funcionarioId}`);
          const data = response.data;
  
          funcionario.cargo_id = data.cargo_id || "";
          funcionario.pessoa_fisica_id = data.pessoa_fisica_id || null;
          funcionario.pessoa_fisica_nome = data.pessoa_fisica_nome || "";
          funcionario.ativo = data.ativo !== undefined ? Boolean(data.ativo) : true;
          funcionario.id = data.id;
        } catch (error) {
          console.error("Erro ao carregar funcionário:", error);
        }
      };
  
      const isFormValid = computed(() => {
        return !!funcionario.pessoa_fisica_id && !!funcionario.cargo_id;
      });
  
      const salvarFuncionario = async () => {
        if (isSubmitting.value) return;
  
        isSubmitting.value = true;
  
        try {
          const method = funcionario.id ? "put" : "post";
          const url = funcionario.id
            ? `/cadastros/funcionarios/${funcionario.id}`
            : "/cadastros/funcionarios";
  
          await axios[method](url, funcionario);
          router.push("/cadastros/funcionarios");
        } catch (error) {
          console.error("Erro ao salvar funcionário:", error);
          errors.backend = error.response?.data?.message || "Ocorreu um erro ao salvar o funcionário.";
        } finally {
          isSubmitting.value = false;
        }
      };
  
      // ✅ Controle do Modal de Seleção de Pessoa Física
      const abrirModal = () => {
        showModal.value = true;
      };
  
      const fecharModal = () => {
        showModal.value = false;
      };
  
      // ✅ 🔹 Adicionando a função `selecionarProprietario` corretamente
      const selecionarProprietario = (payload) => {
        // Mesmo que o payload contenha prospeccaoId e tipoPessoa, para funcionário esses valores não são usados.
        funcionario.pessoa_fisica_id = payload.pessoa.id;
        funcionario.pessoa_fisica_nome = payload.pessoa.nome;
        fecharModal();
      };

      const carregarNovaPessoa = async (newPersonId, newPersonTipo) => {
        try {
          const response = await axios.get(`/cadastros/pessoas-fisicas/${newPersonId}`);
          const novaPessoa = response.data;
          // Atualize os campos do funcionário conforme o tipo
          if (newPersonTipo === 'funcionario' || newPersonTipo === 'proprietario') {
            funcionario.pessoa_fisica_id = novaPessoa.id;
            funcionario.pessoa_fisica_nome = novaPessoa.nome;
          } else {
            console.warn("[FormularioFuncionario] newPersonTipo não confere:", newPersonTipo);
          }
        } catch (error) {
          console.error("Erro ao carregar nova pessoa:", error);
        } finally {
          router.replace({
            query: { ...route.query, newPersonId: undefined, newPersonTipo: undefined }
          });
        }
      };

      // Adicione um watch para monitorar a query e capturar os parâmetros newPersonId e newPersonTipo
      watch(() => route.query, (newQuery) => {
        if (newQuery.newPersonId && newQuery.newPersonTipo) {
          carregarNovaPessoa(newQuery.newPersonId, newQuery.newPersonTipo);
        }
      }, { immediate: true });


  
      const voltar = () => {
        router.push("/cadastros/funcionarios");
      };
  
      onMounted(() => {

        carregarCargos();
        if (route.params.id) {
          carregarFuncionario();
        }
      });
  
      return {
        funcionario,
        isFormValid,
        salvarFuncionario,
        abrirModal,
        fecharModal,
        selecionarProprietario, // ✅ 🔹 Agora a função está sendo retornada corretamente
        voltar,
        cargos,
        isSubmitting,
        showModal,
        errors,
      };
    },
  };
</script>
                
<style scoped>

.input-with-button {
  display: flex;
  align-items: center;
}



.modal-overlay {
display: flex;
align-items: center;
justify-content: center;
position: fixed;
top: 0;
left: 0;
width: 100%;
height: 100%;
background: rgba(0, 0, 0, 0.5);
z-index: 1050;
}

.modal {
position: fixed;
top: 50%;
left: 50%;
transform: translate(-50%, -50%);
background: white;
padding: 20px;
border-radius: 12px;
width: 90%;
max-width: 600px;
max-height: 80%;
overflow-y: auto;
box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
z-index: 1051;
display: block;
}

.modal-header {
display: flex;
justify-content: space-between;
align-items: center;
border-bottom: 1px solid #ddd;
margin-bottom: 15px;
padding-bottom: 10px;
}

.modal-header h5 {
font-size: 1.25rem;
font-weight: bold;
margin: 0;
}

.modal-header button {
padding: 6px 12px; /* Mesmo padding dos outros botões */
font-size: 14px;   /* Tamanho do texto */
font-weight: normal; /* Para ficar consistente com outros botões */
color: #6c757d; /* Cinza suave (similar ao btn-secondary) */
border: 1px solid #ddd; /* Adiciona borda para consistência */
border-radius: 4px;
background-color: #f8f9fa; /* Fundo semelhante ao botão cancelar */
cursor: pointer;
}

.modal-header button:hover {
background-color: #e2e6ea; /* Fundo ao passar o mouse */
border-color: #dae0e5; /* Ajuste de cor da borda ao hover */
}

.modal-body {
padding: 10px 0;
}

</style>